<template>
  <div v-if="!headerPinned" class="tw-fixed tw-left-0 tw-top-0 tw-z-40 tw-h-5 tw-w-full" @mouseenter="showHeader" />

  <header
    ref="root"
    v-bind="$attrs"
    class="tw-fixed tw-left-0 tw-top-0 tw-z-50 tw-flex tw-h-20 tw-w-full tw-items-center tw-justify-between tw-border-white/10 tw-from-white/5 tw-to-white/5 tw-px-4 tw-transition-transform tw-duration-300 sm:tw-space-x-2 lg:tw-border-b lg:tw-bg-dark lg:tw-bg-gradient-to-b"
    :class="headerTransformClass"
    @mouseenter="showHeader"
    @mouseleave="hideHeaderWithDelay"
  >
    <div class="tw-hidden tw-items-center lg:tw-flex">
      <Link href="/" class="tw-flex tw-items-baseline tw-gap-2 tw-no-underline">
        <Heading level="h1" level-class="h2">
          {{ t('nate') }}
          <span class="tw-text-red">{{ t('header_navigation.control_room') }}</span>
        </Heading>
      </Link>
    </div>

    <nav class="tw-hidden tw-items-center tw-gap-3 tw-text-sm lg:tw-flex">
      <Link scheme="underline" text-size="large" href="https://crew.slideslive.com/RoboCam" target="_blank">
        {{ t('header_navigation.about') }}
      </Link>
      <div class="tw-w-0.5 tw-self-stretch tw-bg-purple/25"></div>
      <Link scheme="underline" text-size="large" href="#" @click.prevent="openInstructions">
        {{ t('header_navigation.instructions') }}
      </Link>
      <div class="tw-w-0.5 tw-self-stretch tw-bg-purple/25"></div>
      <Link scheme="underline" text-size="large" href="https://find-my-robo.slideslive.com/" target="_blank">
        {{ t('header_navigation.robocam_locator') }}
      </Link>
      <div class="tw-w-0.5 tw-self-stretch tw-bg-purple/25"></div>
      <Text size="large">{{ appVersion || t('header_navigation.unknown_version') }}</Text>
    </nav>

    <div class="tw-flex tw-w-full tw-items-center tw-justify-between tw-space-x-2 lg:tw-w-auto">
      <Drawer
        class="lg:tw-hidden"
        :overlay="false"
        :trigger-attrs="{
          icon: 'menu',
          shape: 'rounded',
          color: 'semiTransparentWhite',
          iconButton: true,
        }"
      >
        <Checkbox
          type="switch"
          left-label="Allow Control"
          v-model="allowControl"
          :confirm="confirmAllowControl"
          class="tw-px-2 tw-py-4"
        />
        <div class="tw-mt-2 tw-flex tw-flex-col tw-gap-2 tw-px-2 tw-py-1 lg:tw-mt-0">
          <Link href="#" scheme="noUnderline" @click="openSettings" textSize="large">
            {{ t('header_navigation.settings') }}
          </Link>

          <Link
            scheme="noUnderline"
            href="https://grafana.nate.slideslive.dev/public-dashboards/182d99cb0bc24171ac1362f19b577267?orgId=1"
            target="_blank"
            textSize="large"
          >
            {{ t('header_navigation.networking') }}
          </Link>

          <Link scheme="noUnderline" href="https://crew.slideslive.com/RoboCam" textSize="large" target="_blank">
            {{ t('header_navigation.about') }}
          </Link>

          <Link scheme="noUnderline" href="https://find-my-robo.slideslive.com/" textSize="large" target="_blank">
            {{ t('header_navigation.robocam_locator') }}
          </Link>

          <Link href="#" scheme="noUnderline" textSize="large" @click.prevent="openInstructions">
            {{ t('header_navigation.instructions') }}
          </Link>
        </div>

        <template #footer>
          <Text>{{ appVersion || t('header_navigation.unknown_version') }}</Text>
          <CircularButton
            icon="plus"
            color="semiTransparentWhite"
            shape="rounded"
            size="large"
            aria-label="Add"
            @click="openStationManagement"
          />
        </template>
      </Drawer>

      <div class="tw-hidden tw-items-center tw-gap-2 lg:tw-flex">
        <CircularButton
          icon="double-arrow"
          :icon-attrs="{
            class: ['tw-transition-transform tw-duration-300', headerPinned ? '-tw-rotate-90' : 'tw-rotate-90'],
          }"
          color="transparentWhite"
          shape="rounded"
          size="large"
          aria-label="Toggle header pin"
          @click="togglePinned"
        />

        <Checkbox
          type="switch"
          right-label="Allow Control"
          v-model="allowControl"
          :confirm="confirmAllowControl"
          class="tw-px-2"
        />

        <Dropdown
          :overlay="false"
          :trigger-attrs="{
            icon: `${ChartLineSvg}`,
            shape: 'rounded',
            size: 'large',
            color: 'semiTransparentWhite',
            iconButton: true,
          }"
          :menu-attrs="{ class: 'tw-min-w-0 tw-p-2 tw-flex tw-flex-col tw-gap-2' }"
        >
          <template #menu>
            <Link
              scheme="noUnderline"
              href="https://grafana.nate.slideslive.dev/public-dashboards/182d99cb0bc24171ac1362f19b577267?orgId=1"
              target="_blank"
            >
              {{ t('header_navigation.networking') }}
            </Link>
          </template>
        </Dropdown>

        <CircularButton
          icon="plus"
          color="semiTransparentWhite"
          shape="rounded"
          size="large"
          aria-label="Add"
          @click="openStationManagement"
        />

        <div class="tw-relative tw-flex">
          <div
            v-if="bellSeverity"
            :class="['tw-absolute tw-inset-0 tw-rounded', highestSeverity && 'tw-animate-pulse', severityBgClass]"
          />
          <CircularButton
            :icon="bellIcon"
            :color="bellSeverity ? 'transparentWhite' : 'semiTransparentWhite'"
            class="tw-relative"
            shape="rounded"
            size="large"
            :aria-label="$t('notifications_modal.title')"
            @click="notificationsModalStore.openModal()"
          />
        </div>

        <CircularButton
          icon="settings"
          color="semiTransparentWhite"
          shape="rounded"
          size="large"
          aria-label="Settings"
          @click="openSettings"
        />
      </div>

      <Logo class="tw-block tw-w-28 lg:tw-hidden" />
    </div>
  </header>

  <InstructionsModal v-model="instructionsOpen" />
  <StationManagementModal v-model="stationManagementOpen" />
  <SettingsModal v-model="settingsOpen" />
  <NotificationsModal />
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { Checkbox, CircularButton, Drawer, Dropdown, Heading, Link, Logo, Text } from '@slideslive/fuse-kit/vue';

import BellOutlineSvg from '@/assets/mdi/bell-outline.svg';
import BellRingOutlineSvg from '@/assets/mdi/bell-ring-outline.svg';
import ChartLineSvg from '@/assets/mdi/chart-line.svg';
import useNotificationsModalStore from '@/stores/notifications_modal';
import useSettingsStore from '@/stores/settings';

import InstructionsModal from './InstructionsModal.vue';
import NotificationsModal from './NotificationsModal.vue';
import SettingsModal from './SettingsModal.vue';
import StationManagementModal from './StationManagementModal.vue';

const settingsStore = useSettingsStore();
const { allowControl, headerPinned } = storeToRefs(settingsStore);

const notificationsModalStore = useNotificationsModalStore();
const { highestSeverity, mutedHighestSeverity, newMessages, inactiveMessages } = storeToRefs(notificationsModalStore);

const bellRing = ref(false);
let bellTimer = null;
onMounted(() => {
  bellTimer = setInterval(() => {
    bellRing.value = !bellRing.value;
  }, 700);
});
onUnmounted(() => clearInterval(bellTimer));

const bellIcon = computed(() => {
  if (!newMessages.value.length) return BellOutlineSvg;
  return bellRing.value ? BellRingOutlineSvg : BellOutlineSvg;
});
const bellSeverity = computed(() => {
  if (highestSeverity.value) return highestSeverity.value;
  if (mutedHighestSeverity.value) return mutedHighestSeverity.value;
  if (inactiveMessages.value.some((m) => m.level === 'error')) return 'error';
  if (inactiveMessages.value.some((m) => m.level === 'warning')) return 'warning';
  if (inactiveMessages.value.length > 0) return 'info';
  return null;
});
const severityBgClass = computed(() => ({
  'tw-bg-state-err': bellSeverity.value === 'error',
  'tw-bg-state-warn': bellSeverity.value === 'warning',
  'tw-bg-state-info': bellSeverity.value === 'info',
}));

const { t } = useI18n();
const appVersion = import.meta.env.PACKAGE_VERSION;
const instructionsOpen = ref(false);
const stationManagementOpen = ref(false);
const settingsOpen = ref(false);

const hovered = ref(false);
let hoverTimeout;

const headerTransformClass = computed(() => {
  if (headerPinned.value) return 'tw-translate-y-0';
  if (hovered.value) return 'tw-translate-y-0';
  return '-tw-translate-y-full';
});

function clearHoverTimeout() {
  clearTimeout(hoverTimeout);
  hoverTimeout = null;
}

function showHeader() {
  clearHoverTimeout();
  hovered.value = true;
}

function hideHeaderWithDelay() {
  clearHoverTimeout();
  hoverTimeout = setTimeout(() => {
    hovered.value = false;
  }, 300);
}

function togglePinned() {
  headerPinned.value = !headerPinned.value;
}

function confirmAllowControl(nextValue) {
  return (
    !nextValue ||
    window.confirm(
      'Allowing control will let you modify running stations. Make sure you know what you’re doing. Do you want to continue?',
    )
  );
}

function openInstructions() {
  instructionsOpen.value = true;
}

function openStationManagement() {
  stationManagementOpen.value = true;
}

function openSettings() {
  settingsOpen.value = true;
}
</script>
